import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useAuthContext } from "../../context/AuthContext";
import ActiveCampaign from "../../services/activeCampaign";
import { requests } from "../../services/requests";
import apiMagicLink from "../../services/apiMagicLink";

import Alert from "../../components/Alert";
import AnimationContainer from "../../components/AnimationContainer";
import LoginContainer from "../../components/LoginContainer";
import LoginPageTitle from "../../components/LoginPageTitle";
import Input from "../../components/Input";
import Button from "../../components/Button";

import * as S from "./styles";
import { translateAmplifyErrorMessage } from "../../util/messages";

const schema = yup.object({
  email: yup
    .string()
    .required("Preencha um email válido")
    .email("Preencha um email válido")
    .trim()
    .lowercase(),
});

function Login() {
  const history = useHistory();
  const location = useLocation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
  });
  const { answerCustomChallenge, setIsLoggedIn } = useAuthContext();

  const [loading, setLoading] = useState({ magicLink: false, password: false });
  const [alert, setAlert] = useState({});
  const isLoading = Object.values(loading).some((v) => v === true);

  const params = new URLSearchParams(location.search);
  const [emailBase64, challengeParam, changePassword] = [
    params.get("email"),
    params.get("challenge"),
    params.get("pass"),
  ];
  const emailParam = !!emailBase64 ? atob(emailBase64) : "";

  useEffect(() => {
    async function answerChallenge() {
      setLoadingMagicLink(true);
      try {
        const response = await answerCustomChallenge(
          emailParam,
          challengeParam
        );

        if (response && !response.signInUserSession)
          throw Error("Code expired");

        setIsLoggedIn(true);
        ActiveCampaign.onSuccessLogin(emailParam);

        if (changePassword) {
          return history.push("/alterar-senha");
        }
        history.push("/explore");
      } catch (error) {
        const { message } = translateAmplifyErrorMessage(error?.message);
        setAlert({
          type: "error",
          message,
        });
      } finally {
        setLoadingMagicLink(false);
      }
    }

    if (emailParam) {
      setValue("email", emailParam);
      if (challengeParam) {
        answerChallenge();
      }
    }
  }, []);

  const setLoadingMagicLink = (value) =>
    setLoading({ magicLink: value, password: false });

  const setLoadingPassword = (value) =>
    setLoading({ magicLink: false, password: value });

  const verifyUser = async (email) => {
    try {
      await requests.verifyUser(email);
      return true;
    } catch {
      const message = (
        <>
          Não foi encontrada uma conta com esse endereço de e-mail. Tente
          novamente ou&nbsp;
          <Link to={"/explore/cadastro"}>crie uma nova conta.</Link>
        </>
      );
      setAlert({
        message,
        type: "error",
      });
      return false;
    }
  };

  const handleLoginWithMagicLink = async (formEmail) => {
    const isValid = await trigger("email");

    if (!isValid) {
      return;
    }
    setLoadingMagicLink(true);
    const userExists = await verifyUser(formEmail);
    if (!userExists) {
      setLoadingMagicLink(false);
      return;
    }

    try {
      await apiMagicLink.post("/signin", {
        email: formEmail,
        pass: false,
        appExplore: true,
      });

      history.push("/explore/entrar/info", {
        email: formEmail,
        type: "login",
      });
    } catch (error) {
      ActiveCampaign.onFailedLogin(formEmail);

      setAlert({
        message:
          "Houve um problema, por favor, verifique os dados e tente novamente.",
        type: "error",
      });
    } finally {
      setLoadingMagicLink(false);
    }
  };

  const onSubmit = async (data) => {
    setLoadingPassword(true);
    const userExists = await verifyUser(data.email);
    if (!userExists) {
      setLoadingPassword(false);
      return;
    }

    history.push("/explore/entrar/senha", {
      email: data.email,
      changePassword,
    });
  };

  return (
    <LoginContainer>
      <S.LoginWrapper>
        <AnimationContainer show={!!alert.message}>
          <Alert variant={alert.type} text={alert.message} />
        </AnimationContainer>
        <LoginPageTitle
          title={"Entrar na conta"}
          subtitle={
            <>
              Digite o seu <b>e-mail</b> e escolha como quer acessar a sua conta
              gratuita.
            </>
          }
        />

        <S.Form onSubmit={handleSubmit(onSubmit)}>
          <Input
            label={"E-mail"}
            inputName={"email"}
            registerFunction={register}
            errors={errors}
            control={control}
          />
          <Button
            variant={"white"}
            size={"full"}
            type="submit"
            disabled={isLoading}
            loading={loading.password}
          >
            Entrar com senha
          </Button>
        </S.Form>
        <Button
          variant={"transparent"}
          size={"full"}
          onClick={() =>
            handleLoginWithMagicLink(getValues("email").toLowerCase())
          }
          disabled={isLoading}
          loading={loading.magicLink}
        >
          Receber acesso por e-mail
        </Button>
      </S.LoginWrapper>
    </LoginContainer>
  );
}

export default Login;
